import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const leadership = [
  {
    name: "Suresh Sapkota",
    role: "CEO",
    initials: "SS",
    bio: "Suresh Sapkota provides strategic leadership and overall direction for 3E Entertainment. With decades of involvement in the Nepali entertainment and community landscape, he oversees operations, partnerships, productions, and long-term growth initiatives.",
  },
  {
    name: "Anjana Budhathoki",
    role: "Founder & Managing Director",
    initials: "AB",
    bio: "Anjana Budhathoki leads strategic planning, brand direction, community partnerships, and charitable initiatives. Her leadership bridges culture, business, and social impact, ensuring that 3E Entertainment remains purpose-driven and deeply connected to the Nepali diaspora.",
  },
  {
    name: "Prayash Bhusal",
    role: "Senior Coordinator",
    initials: "PB",
    bio: "Prayash Bhusal leads youth-focused initiatives that help second-generation Nepali youth participate in culture with confidence, pride, and connection.",
  },
  {
    name: "Sagar Sapkota",
    role: "Youth Coordinator",
    initials: "SS",
    bio: "Sagar Sapkota drives youth engagement programs, connecting young members of the Nepali diaspora to cultural experiences and community events.",
  },
  {
    name: "Prabin Bhattarai",
    role: "Media Coordinator",
    initials: "PB",
    bio: "Prabin Bhattarai oversees media relations, content creation, and communications, ensuring 3E Entertainment's story reaches and resonates with audiences across platforms.",
  },
  {
    name: "Pratik Bhusal",
    role: "Marketing Coordinator",
    initials: "PB",
    bio: "Pratik Bhusal supports marketing strategy, campaign coordination, and audience outreach, helping 3E Entertainment connect each event with the right community and cultural audience.",
  },
  {
    name: "Anjan Budhathoki",
    role: "Nepal Representative",
    initials: "AB",
    bio: "Based in Nepal, Anjan Budhathoki supports artist coordination, partner relations, and cross-border cultural collaboration.",
  },
  {
    name: "Prabha Deuja",
    role: "Charity & Community Partnership Lead",
    initials: "PD",
    bio: "Prabha Deuja leads nonprofit partnerships and charitable collaborations, helping ensure that 3E Entertainment's giving commitment remains meaningful, transparent, and impactful.",
  },
];

const Team = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="team-page"
    >
      {/* Hero */}
      <section className="team-hero">
        <div className="th-overlay" />
        <motion.div
          className="th-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            The People Behind 3E Entertainment
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Behind every 3E Entertainment experience is a team committed to
            excellence, authenticity, and community impact.
          </motion.p>
        </motion.div>
      </section>

      {/* Intro */}
      <section className="team-intro-section">
        <div className="t-container">
          <motion.div
            className="team-intro-text"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2>Decades of Experience, One Shared Mission</h2>
            <p>
              Our leadership brings decades of experience in cultural
              programming, artist relations, large-scale event production, and
              community engagement. While 3E Entertainment was officially
              established in 2021, our roots go much deeper — our leadership
              has been shaping the Nepali entertainment landscape in the United
              States since 2000, building trust, producing landmark events, and
              creating platforms for artists and communities long before Nepali
              entertainment became mainstream in America.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Leadership Cards */}
      <section className="leadership-section">
        <div className="t-container">
          <div className="leadership-grid">
            {leadership.map((member, index) => (
              <motion.div
                key={member.name}
                className="leader-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -6, transition: { duration: 0.3 } }}
              >
                <div className="leader-avatar">{member.initials}</div>
                <div className="leader-info">
                  <h3>{member.name}</h3>
                  <span className="leader-role">{member.role}</span>
                  <div className="leader-divider" />
                  <p>{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="team-cta-section">
        <div className="tc-overlay" />
        <motion.div
          className="tc-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2>Work With Us</h2>
          <p>
            Interested in collaborating, joining our mission, or learning more
            about 3E Entertainment? We would love to connect with passionate
            individuals and organizations who share our vision.
          </p>
          <div className="tc-btns">
            <Link to="/contact" className="tc-btn-primary">
              Get In Touch <ArrowRight size={16} />
            </Link>
            <Link to="/about" className="tc-btn-secondary">
              Our Story
            </Link>
          </div>
        </motion.div>
      </section>

      <style>{`
        .team-page { overflow-x: hidden; }

        .team-hero {
          position: relative;
          min-height: 65vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("/images/about-hero.jpg") center/cover no-repeat;
        }

        .th-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.82);
        }

        .th-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 0 2rem;
        }


        .th-content h1 {
          font-size: clamp(2.5rem, 5.5vw, 3.8rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1.5rem;
          line-height: 1.1;
        }

        .th-content p {
          font-size: 1.15rem;
          color: rgba(255, 255, 255, 0.85);
          line-height: 1.7;
          max-width: 560px;
          margin: 0 auto;
        }

        .t-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }


        /* Intro */
        .team-intro-section {
          padding: 6rem 0;
          background: #fff;
        }

        .team-intro-text {
          max-width: 760px;
          margin: 0 auto;
          text-align: center;
        }

        .team-intro-text h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1.5rem;
          line-height: 1.1;
        }

        .team-intro-text p {
          font-size: 1.05rem;
          color: #555;
          line-height: 1.85;
          margin: 0;
        }

        /* Leadership */
        .leadership-section {
          padding: 2rem 0 7rem;
          background: #fff;
        }

        .leadership-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.75rem;
        }

        .leader-card {
          background: #fff;
          border: 1px solid #f0f0f0;
          border-radius: 20px;
          padding: 2.5rem 2rem;
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          cursor: default;
        }

        .leader-card:hover {
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
          border-color: #C72631;
        }

        .leader-avatar {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #C72631;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }

        .leader-info h3 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #0a0a0a;
          margin: 0 0 0.4rem;
        }

        .leader-role {
          display: block;
          font-size: 0.78rem;
          font-weight: 600;
          color: #C72631;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 1.25rem;
        }

        .leader-divider {
          width: 35px;
          height: 3px;
          background: #C72631;
          border-radius: 2px;
          margin-bottom: 1.25rem;
        }

        .leader-info p {
          font-size: 0.93rem;
          color: #666;
          line-height: 1.75;
          margin: 0;
        }

        /* CTA */
        .team-cta-section {
          position: relative;
          padding: 7rem 2rem;
          background: url("/images/R5BI6429.webp") center/cover no-repeat;
          text-align: center;
        }

        .tc-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .tc-content {
          position: relative;
          z-index: 2;
          max-width: 660px;
          margin: 0 auto;
        }

        .tc-content h2 {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 900;
          color: #fff;
          margin: 0 0 1.25rem;
        }

        .tc-content p {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.75;
          margin: 0 0 2.5rem;
        }

        .tc-btns {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .tc-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #C72631;
          color: #fff;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: background 0.3s cubic-bezier(0.22, 1, 0.36, 1), transform 0.3s ease;
        }

        .tc-btn-primary:hover {
          background: #a01f28;
          transform: translateY(-3px);
        }

        .tc-btn-secondary {
          display: inline-flex;
          align-items: center;
          background: transparent;
          color: #fff;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1px;
          border: 2px solid rgba(255, 255, 255, 0.4);
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .tc-btn-secondary:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: #fff;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .leadership-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .team-intro-section,
          .leadership-section {
            padding: 4rem 0;
          }

          .t-container {
            padding: 0 1.5rem;
          }

          .leadership-grid {
            grid-template-columns: 1fr;
            max-width: 460px;
            margin: 0 auto;
          }

          .tc-btns {
            flex-direction: column;
            align-items: center;
          }

          .tc-btn-primary,
          .tc-btn-secondary {
            width: 100%;
            max-width: 280px;
            justify-content: center;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Team;
